import { create } from 'zustand';

import {
  AgreementDTO,
  CommentDTO,
  DirectionType,
  DirectoryDTO,
  FileDTO,
  InsuranceAreaDTO,
  InsuranceObjectDTO,
  TagDTO,
} from '@/types/api-types';

import { apiService, ReportAnalytics } from '@/services/api-service';

import { getInsuranceObjectIdFromInsuranceAreaId } from '@/utils/get-agreement-id-from-insurance-area-id';

import { useFileExplorerStore } from './fileExplorerStore';
import { useGlobalStore } from './globalStore';

interface ApiManagerState {
  // Loading states
  isLoading: {
    [key: string]: boolean;
  };

  totalActiveAgreements: number;

  // State
  directories: DirectoryDTO[];
  insuranceObjects: InsuranceObjectDTO[];
  agreements: AgreementDTO[];
  files: FileDTO[];
  tags: TagDTO[];
  comments: CommentDTO[];
  insuranceAreas: InsuranceAreaDTO[];

  reportAnalytics: ReportAnalytics;

  // Auto-fetching selectors

  setTotalActiveAgreements: (total: number) => void;

  getInsuranceObjects: (
    uuids?: string[],
    extra?: {
      take?: number;
      skip?: number;
      parent_uuid?: string;
    },
  ) => Promise<InsuranceObjectDTO[]>;
  getAgreements: (
    uuids?: string[],
    extra?: {
      take?: number;
      skip?: number;
      parent_uuid?: string;
      exclude_archived?: boolean;
    },
  ) => Promise<AgreementDTO[]>;
  getInsuranceArea: (
    uuids?: InsuranceAreaDTO['uuid'][],
    extra?: {
      take?: number;
      skip?: number;
      parent_uuid?: string;
      exclude_archived?: boolean;
    },
  ) => Promise<InsuranceAreaDTO[]>;
  setAgreements: (agreements: AgreementDTO[]) => void;
  updateAgreements: (agreements: AgreementDTO[]) => Promise<AgreementDTO[]>;
  getAutoFetchFiles: (
    uuids?: string[],
    extra?: { take?: number; skip?: number; parent_uuid?: string },
  ) => Promise<FileDTO[]>;
  getAutoFetchTags: () => Promise<TagDTO[]>;
  getAutoFetchComments: () => Promise<CommentDTO[]>;

  // Directories
  getDirectories: (take?: number, skip?: number) => Promise<DirectoryDTO[]>;
  setDirectories: (directories: DirectoryDTO[]) => void;
  createDirectory: (directory: Omit<DirectoryDTO, 'uuid'>) => Promise<string>;
  updateDirectory: (
    uuid: string,
    directory: Partial<DirectoryDTO>,
  ) => Promise<DirectoryDTO>;

  createInsuranceObject: (
    insuranceObject: Omit<InsuranceObjectDTO, 'uuid'>,
  ) => Promise<InsuranceObjectDTO['uuid']>;

  getAgreement: (id: string) => Promise<AgreementDTO>;
  updateAgreement: (
    id: string,
    agreement: Partial<AgreementDTO>,
  ) => Promise<AgreementDTO>;
  createAgreement: (
    agreement: Omit<AgreementDTO, 'uuid'>,
  ) => Promise<AgreementDTO['uuid']>;

  // Files
  getFiles: (
    uuids?: string[],
    extra?: { take?: number; skip?: number; parent_uuid?: string },
  ) => Promise<FileDTO[]>;
  setFiles: (files: FileDTO[]) => void;
  createFile: (file: Omit<FileDTO, 'uuid'>) => Promise<FileDTO>;
  deleteFile: (fileId: string) => Promise<void>;
  updateFile: (file_uuid: string, new_file_data: FileDTO) => Promise<FileDTO>;
  updateFileAfterMove: (
    file_uuid: string,
    agreement_uuid: string,
  ) => Promise<void>;
  // Tags
  getTags: () => Promise<void>;

  // Comments
  getComments: () => Promise<void>;

  // Report analytics
  getReportAnalytics: () => Promise<ReportAnalytics>;
}

export const useApiManagerStore = create<ApiManagerState>((set, get) => ({
  // Loading states
  isLoading: {},

  totalActiveAgreements: 0,

  // Initial state
  users: [],
  directories: [],
  insuranceObjects: [],
  agreements: [],
  insuranceAreas: [],
  files: [],
  tags: [],
  comments: [],
  reportAnalytics: {
    effectivenessReport: {
      title: '',

      description: '',
      metrics: {
        lateUploadedFiles: '',
        failedValidationFiles: '',
      },
    },
    vinReport: {
      title: '',
      description: '',
      metrics: '',
    },
  },

  setTotalActiveAgreements: (total: number) => {
    set({ totalActiveAgreements: total });
  },

  setAgreements: (agreements: AgreementDTO[]) => {
    set({ agreements });
  },
  updateAgreements: async (agreements: AgreementDTO[]) => {
    const updatedAgreements = await apiService.bulkUpdateAgreements(agreements);

    const oldAgreements = get().agreements;
    const newAgreements = updatedAgreements.map((agreement) => {
      const oldAgreement = oldAgreements.find((a) => a.uuid === agreement.uuid);
      if (!oldAgreement) return agreement;
      return { ...oldAgreement, ...agreement };
    });
    set({ agreements: newAgreements });
    return newAgreements;
  },
  setDirectories: (directories: DirectoryDTO[]) => {
    set({ directories });
  },

  getInsuranceObjects: async (
    uuids?: string[],
    extra?: {
      take?: number;
      skip?: number;
      parent_uuid?: string;
    },
  ) => {
    const oldInsuranceObjects = get().insuranceObjects;

    if (!uuids || uuids.length === 0) {
      if (extra?.parent_uuid) {
        if (extra.take !== undefined && extra.skip !== undefined) {
          const insuranceObjectsByParentUuid = oldInsuranceObjects.filter(
            (obj) => obj.directory === extra.parent_uuid,
          );

          const insuranceObjectsToFetch = insuranceObjectsByParentUuid.slice(
            extra.skip,
            extra.skip + extra.take,
          );
          if (insuranceObjectsToFetch.length === extra.take) {
            return insuranceObjectsToFetch;
          }
        }

        const data = await apiService.getInsuranceObjects(undefined, extra);
        const newInsuranceObjects = data.filter(
          (obj) => !oldInsuranceObjects.some((a) => a.uuid === obj.uuid),
        );
        const mergedInsuranceObjects = [
          ...oldInsuranceObjects,
          ...newInsuranceObjects,
        ];
        set({ insuranceObjects: mergedInsuranceObjects });
        return data;
      }

      const data = await apiService.getInsuranceObjects(undefined, extra);

      const newInsuranceObjects = data.filter(
        (obj) => !oldInsuranceObjects.some((a) => a.uuid === obj.uuid),
      );
      const mergedInsuranceObjects = [
        ...oldInsuranceObjects,
        ...newInsuranceObjects,
      ];
      set({ insuranceObjects: mergedInsuranceObjects });
      return data;
    }

    // Check if we already have all requested insurance objects
    if (
      oldInsuranceObjects.length > 0 &&
      uuids.every((uuid) =>
        oldInsuranceObjects.some((obj) => obj.uuid === uuid),
      )
    ) {
      return oldInsuranceObjects.filter((obj) => uuids.includes(obj.uuid));
    }

    // Fetch missing insurance objects
    if (oldInsuranceObjects.length > 0) {
      const missingInsuranceObjects = uuids.filter(
        (uuid) => !oldInsuranceObjects.some((obj) => obj.uuid === uuid),
      );
      const data = await apiService.getInsuranceObjects(
        missingInsuranceObjects,
        extra,
      );
      const newInsuranceObjects = data.filter(
        (obj) => !oldInsuranceObjects.some((a) => a.uuid === obj.uuid),
      );
      const mergedInsuranceObjects = [
        ...oldInsuranceObjects,
        ...newInsuranceObjects,
      ];
      set({ insuranceObjects: mergedInsuranceObjects });
      return mergedInsuranceObjects.filter((obj) => uuids.includes(obj.uuid));
    }
    // Fetch the specific insurance objects directly from API without updating store
    const data = await apiService.getInsuranceObjects(uuids, extra);
    const newInsuranceObjects = data.filter(
      (obj) => !oldInsuranceObjects.some((a) => a.uuid === obj.uuid),
    );
    const mergedInsuranceObjects = [
      ...oldInsuranceObjects,
      ...newInsuranceObjects,
    ];
    set({ insuranceObjects: mergedInsuranceObjects });
    return mergedInsuranceObjects.filter((obj) => uuids.includes(obj.uuid));
  },

  getAgreements: async (
    uuids?: string[],
    extra?: {
      take?: number;
      skip?: number;
      parent_uuid?: string;
      exclude_archived?: boolean;
    },
  ) => {
    const oldAgreements = get().agreements;

    if (!uuids || uuids.length === 0) {
      if (extra?.parent_uuid) {
        if (extra.take !== undefined && extra.skip !== undefined) {
          const agreementsByParentUuid = oldAgreements.filter(
            (agreement) => agreement.insurance_object === extra.parent_uuid,
          );

          const agreementsToFetch = agreementsByParentUuid.slice(
            extra.skip,
            extra.skip + extra.take,
          );
          if (agreementsToFetch.length === extra.take) {
            return agreementsToFetch;
          }
        }

        const data = await apiService.getAgreements(undefined, extra);
        const newAgreements = data.filter(
          (agreement) => !oldAgreements.some((a) => a.uuid === agreement.uuid),
        );
        const mergedAgreements = [...oldAgreements, ...newAgreements];
        set({ agreements: mergedAgreements });
        return data;
      }

      const data = await apiService.getAgreements(undefined, extra);

      const newAgreements = data.filter(
        (agreement) => !oldAgreements.some((a) => a.uuid === agreement.uuid),
      );
      const mergedAgreements = [...oldAgreements, ...newAgreements];
      set({ agreements: mergedAgreements });
      return data;
    }

    // Check if we already have all requested agreements
    if (
      oldAgreements.length > 0 &&
      uuids.every((uuid) =>
        oldAgreements.some((agreement) => agreement.uuid === uuid),
      )
    ) {
      return oldAgreements.filter((agreement) =>
        uuids.includes(agreement.uuid),
      );
    }

    // Fetch missing agreements
    if (oldAgreements.length > 0) {
      const missingAgreements = uuids.filter(
        (uuid) => !oldAgreements.some((agreement) => agreement.uuid === uuid),
      );
      const data = await apiService.getAgreements(missingAgreements, extra);
      const newAgreements = data.filter(
        (agreement) => !oldAgreements.some((a) => a.uuid === agreement.uuid),
      );
      const mergedAgreements = [...oldAgreements, ...newAgreements];
      set({ agreements: mergedAgreements });
      return mergedAgreements.filter((agreement) =>
        uuids.includes(agreement.uuid),
      );
    }
    // Fetch the specific agreements directly from API without updating store
    const data = await apiService.getAgreements(uuids, extra);
    const newAgreements = data.filter(
      (agreement) => !oldAgreements.some((a) => a.uuid === agreement.uuid),
    );
    const mergedAgreements = [...oldAgreements, ...newAgreements];
    set({ agreements: mergedAgreements });
    return mergedAgreements.filter((agreement) =>
      uuids.includes(agreement.uuid),
    );
  },

  getInsuranceArea: async (
    uuids?: InsuranceAreaDTO['uuid'][],
    extra?: {
      take?: number;
      skip?: number;
      parent_uuid?: string;
      exclude_archived?: boolean;
    },
  ) => {
    const oldInsuranceAreas = get().insuranceAreas;
    const { indexes } = useGlobalStore.getState();

    if (!uuids || uuids.length === 0) {
      if (extra?.parent_uuid) {
        if (extra.take !== undefined && extra.skip !== undefined) {
          const insuranceAreasByParentUuid = oldInsuranceAreas.filter(
            (area) => area.insurance_area === extra.parent_uuid,
          );

          const insuranceAreasToFetch = insuranceAreasByParentUuid.slice(
            extra.skip,
            extra.skip + extra.take,
          );
          if (insuranceAreasToFetch.length === extra.take) {
            return insuranceAreasToFetch;
          }
        }

        const data = await apiService.getAgreements(undefined, extra);

        const newInsuranceAreasEntities = data.reduce((acc, agreement) => {
          const indexOfAgreement = indexes.find(
            (index) => index.uuid === agreement.index,
          );

          if (!indexOfAgreement) return acc;

          const newInsuranceAreaUuid =
            `${indexOfAgreement.insurance_area}_${agreement.insurance_object}` as `${DirectionType}_${string}`;

          if (acc.find((area) => area.uuid === newInsuranceAreaUuid)) {
            return acc;
          }

          acc.push({
            uuid: newInsuranceAreaUuid,
            name: indexOfAgreement.insurance_area,
            insurance_area: indexOfAgreement.insurance_area,
            insurance_object: agreement.insurance_object,
            created: agreement.created,
            modified: agreement.modified,
          });

          return acc;
        }, [] as InsuranceAreaDTO[]);

        const newInsuranceAreas = newInsuranceAreasEntities.filter(
          (area) => !oldInsuranceAreas.some((a) => a.uuid === area.uuid),
        );

        const mergedInsuranceAreas = [
          ...oldInsuranceAreas,
          ...newInsuranceAreas,
        ];
        set({ insuranceAreas: mergedInsuranceAreas });
        return newInsuranceAreasEntities;
      }

      const data = await apiService.getAgreements(undefined, extra);
      const newInsuranceAreasEntities = data.reduce((acc, agreement) => {
        const indexOfAgreement = indexes.find(
          (index) => index.uuid === agreement.index,
        );

        if (!indexOfAgreement) return acc;

        const newInsuranceAreaUuid =
          `${indexOfAgreement.insurance_area}_${agreement.insurance_object}` as `${DirectionType}_${string}`;

        if (acc.find((area) => area.uuid === newInsuranceAreaUuid)) {
          return acc;
        }

        acc.push({
          uuid: newInsuranceAreaUuid,
          name: indexOfAgreement.insurance_area,
          insurance_area: indexOfAgreement.insurance_area,
          insurance_object: agreement.insurance_object,
          created: agreement.created,
          modified: agreement.modified,
        });

        return acc;
      }, [] as InsuranceAreaDTO[]);

      const newInsuranceAreas = newInsuranceAreasEntities.filter(
        (area) => !oldInsuranceAreas.some((a) => a.uuid === area.uuid),
      );
      const mergedInsuranceAreas = [...oldInsuranceAreas, ...newInsuranceAreas];
      set({ insuranceAreas: mergedInsuranceAreas });
      return newInsuranceAreasEntities;
    }

    // Check if we already have all requested agreements
    if (
      oldInsuranceAreas.length > 0 &&
      uuids.every((uuid) =>
        oldInsuranceAreas.some((area) => area.uuid === uuid),
      )
    ) {
      return oldInsuranceAreas.filter((area) => uuids.includes(area.uuid));
    }

    // Fetch missing agreements
    if (oldInsuranceAreas.length > 0) {
      const missingInsuranceAreas = uuids
        .filter((uuid) => !oldInsuranceAreas.some((area) => area.uuid === uuid))
        .map((uuid) => getInsuranceObjectIdFromInsuranceAreaId(uuid));
      const data = await apiService.getAgreements(missingInsuranceAreas, extra);
      const newInsuranceAreasEntities = data.reduce((acc, agreement) => {
        const indexOfAgreement = indexes.find(
          (index) => index.uuid === agreement.index,
        );

        if (!indexOfAgreement) return acc;

        const newInsuranceAreaUuid =
          `${indexOfAgreement.insurance_area}_${agreement.insurance_object}` as `${DirectionType}_${string}`;

        if (acc.find((area) => area.uuid === newInsuranceAreaUuid)) {
          return acc;
        }

        acc.push({
          uuid: newInsuranceAreaUuid,
          name: indexOfAgreement.insurance_area,
          insurance_area: indexOfAgreement.insurance_area,
          insurance_object: agreement.insurance_object,
          created: agreement.created,
          modified: agreement.modified,
        });

        return acc;
      }, [] as InsuranceAreaDTO[]);
      const newInsuranceAreas = newInsuranceAreasEntities.filter(
        (area) => !oldInsuranceAreas.some((a) => a.uuid === area.uuid),
      );
      const mergedInsuranceAreas = [...oldInsuranceAreas, ...newInsuranceAreas];
      set({ insuranceAreas: mergedInsuranceAreas });
      return mergedInsuranceAreas.filter((area) => uuids.includes(area.uuid));
    }
    // Fetch the specific agreements directly from API without updating store
    const data = await apiService.getAgreements(uuids, extra);
    const newInsuranceAreasEntities = data.reduce((acc, agreement) => {
      const indexOfAgreement = indexes.find(
        (index) => index.uuid === agreement.index,
      );

      if (!indexOfAgreement) return acc;

      const newInsuranceAreaUuid =
        `${indexOfAgreement.insurance_area}_${agreement.insurance_object}` as `${DirectionType}_${string}`;

      if (acc.find((area) => area.uuid === newInsuranceAreaUuid)) {
        return acc;
      }

      acc.push({
        uuid: newInsuranceAreaUuid,
        name: indexOfAgreement.insurance_area,
        insurance_area: indexOfAgreement.insurance_area,
        insurance_object: agreement.insurance_object,
        created: agreement.created,
        modified: agreement.modified,
      });

      return acc;
    }, [] as InsuranceAreaDTO[]);
    const newInsuranceAreas = newInsuranceAreasEntities.filter(
      (area) => !oldInsuranceAreas.some((a) => a.uuid === area.uuid),
    );
    const mergedInsuranceAreas = [...oldInsuranceAreas, ...newInsuranceAreas];
    set({ insuranceAreas: mergedInsuranceAreas });
    return mergedInsuranceAreas.filter((area) => uuids.includes(area.uuid));
  },

  getAutoFetchFiles: async (
    uuids?: string[],
    extra?: { take?: number; skip?: number; parent_uuid?: string },
  ) => {
    const oldFiles = get().files;

    if (!uuids || uuids.length === 0) {
      if (extra?.parent_uuid) {
        // Fetch files by parent_uuid (agreement UUID)
        const data = await get().getFiles(undefined, extra);
        const newFiles = data.filter((file) => !oldFiles.includes(file));
        const mergedFiles = [...oldFiles, ...newFiles];
        set({ files: mergedFiles });
        return mergedFiles.filter(
          (file) => extra?.parent_uuid === file.agreement,
        );
      }
      return [];
    }
    if (
      oldFiles.length > 0 &&
      uuids.every((uuid) => oldFiles.some((file) => file.uuid === uuid))
    ) {
      return oldFiles.filter((file) => uuids.includes(file.uuid));
    }
    // Fetch missing files
    if (oldFiles.length > 0) {
      const missingFiles = uuids.filter(
        (uuid) => !oldFiles.some((file) => file.uuid === uuid),
      );
      const data = await get().getFiles(missingFiles, extra);
      const newFiles = data.filter((file) => !oldFiles.includes(file));
      const mergedFiles = [...oldFiles, ...newFiles];
      set({ files: mergedFiles });
      return mergedFiles.filter((file) => uuids.includes(file.uuid));
    }
    // Fetch the specific files directly from API without updating store
    const files = await get().getFiles(uuids, extra);
    // Merge old files with new files without duplicates
    const newFiles = files.filter((file) => !oldFiles.includes(file));

    set({ files: [...oldFiles, ...newFiles] });
    return [...oldFiles, ...newFiles].filter((file) =>
      uuids.includes(file.uuid),
    );
  },

  getAutoFetchTags: async () => {
    if (get().tags.length === 0) {
      await get().getTags();
    }
    return get().tags;
  },

  getAutoFetchComments: async () => {
    if (get().comments.length === 0) {
      await get().getComments();
    }
    return get().comments;
  },

  // Directories
  getDirectories: async (take?: number, skip?: number) => {
    const oldDirectories = get().directories;
    if (take !== undefined && skip !== undefined) {
      return oldDirectories.slice(skip, skip + take);
    }
    return oldDirectories;
  },
  createDirectory: async (directory: Omit<DirectoryDTO, 'uuid'>) => {
    const data = await apiService.createDirectory(directory);
    set((state) => ({
      directories: [data, ...state.directories],
    }));
    return data.uuid;
  },
  updateDirectory: async (uuid: string, directory: Partial<DirectoryDTO>) => {
    const data = await apiService.updateDirectory(uuid, directory);
    set((state) => ({
      directories: state.directories.map((directory) =>
        directory.uuid === uuid ? data : directory,
      ),
    }));
    return data;
  },

  createInsuranceObject: async (
    insuranceObject: Omit<InsuranceObjectDTO, 'uuid'>,
  ) => {
    const data = await apiService.createInsuranceObject(insuranceObject);
    set((state) => ({
      insuranceObjects: [data, ...state.insuranceObjects],
    }));
    return data.uuid;
  },

  // Agreements
  createAgreement: async (agreement: Omit<AgreementDTO, 'uuid'>) => {
    const data = await apiService.createAgreement(agreement);
    set((state) => ({
      agreements: [data, ...state.agreements],
    }));
    return data.uuid;
  },
  getAgreement: async (id: string) => {
    const oldAgreements = get().agreements;
    const oldAgreement = oldAgreements.find(
      (agreement) => agreement.uuid === id,
    );
    if (oldAgreement) {
      return oldAgreement;
    }
    return await apiService.getAgreement(id);
  },

  updateAgreement: async (uuid: string, agreement: Partial<AgreementDTO>) => {
    const updatedAgreement = await apiService.updateAgreement(uuid, agreement);
    set((state) => ({
      agreements: state.agreements.map((agreement) =>
        agreement.uuid === uuid ? updatedAgreement : agreement,
      ),
    }));
    const agreementsState = useFileExplorerStore.getState().agreementsState;
    const oldAgreement = agreementsState.entities[uuid];

    useFileExplorerStore.setState({
      agreementsState: {
        ...agreementsState,
        entities: {
          ...agreementsState.entities,
          [uuid]: {
            ...oldAgreement,
            ...updatedAgreement,
          },
        },
      },
    });

    return updatedAgreement;
  },

  // Files
  getFiles: async (
    uuids?: string[],
    extra?: { take?: number; skip?: number; parent_uuid?: string },
  ) => {
    const data = await apiService.getFiles(uuids, extra);
    const oldFiles = get().files;
    const newFiles = data.filter((file) => !oldFiles.includes(file));
    set({ files: [...oldFiles, ...newFiles] });
    return [...oldFiles, ...newFiles];
  },
  setFiles: (files: FileDTO[]) => {
    set({ files });
  },
  createFile: async (file: Omit<FileDTO, 'uuid'>) => {
    const data = await apiService.createFile(file);
    set((state) => ({
      files: [...state.files, data],
    }));
    return data;
  },
  deleteFile: async (fileId: string) => {
    await apiService.deleteFile(fileId);
    const explorerState = useFileExplorerStore.getState();

    const { [fileId]: _, ...remainingEntities } =
      explorerState.filesState.entities;
    explorerState.setFilesState({
      ...explorerState.filesState,
      entities: remainingEntities,
    });
    set((state) => ({
      files: state.files.filter((file) => file.uuid !== fileId),
    }));
  },
  updateFile: async (file_uuid: string, new_file_data: FileDTO) => {
    const newFile = await apiService.updateFile(file_uuid, new_file_data);
    set((state) => ({
      files: state.files.map((file) =>
        file.uuid === file_uuid ? { ...file, ...newFile } : file,
      ),
    }));
    return newFile;
  },

  updateFileAfterMove: async (file_uuid: string, agreement_uuid: string) => {
    const file = await apiService.getFile(file_uuid);
    const oldAgreementUuid = file.agreement;
    const newFileWithAgreementUuid = {
      ...file,
      agreement: agreement_uuid,
    };

    const newFile = await apiService.updateFile(
      file_uuid,
      newFileWithAgreementUuid,
    );
    set((state) => ({
      files: state.files.map((file) =>
        file.uuid === file_uuid ? { ...file, ...newFile } : file,
      ),
    }));
    const filesState = useFileExplorerStore.getState().filesState;
    const agreementsState = useFileExplorerStore.getState().agreementsState;
    const setAgreementsState =
      useFileExplorerStore.getState().setAgreementsState;
    const oldFile = filesState.entities[file_uuid];
    const oldAgreement = agreementsState.entities[oldAgreementUuid];
    const newAgreement = agreementsState.entities[agreement_uuid];

    // Check if the moved file has validation errors
    const movedFileHasErrors =
      oldFile?.validation_errors && oldFile.validation_errors.length > 0;

    // Recalculate has_file_errors for old agreement:
    // Check if any remaining files in the old agreement have errors
    const oldAgreementRemainingFiles = (oldAgreement?.files || []).filter(
      (fId) => fId !== file_uuid,
    );
    const oldAgreementStillHasErrors = oldAgreementRemainingFiles.some(
      (fId) => {
        const f = filesState.entities[fId];
        return f?.validation_errors && f.validation_errors.length > 0;
      },
    );

    // Update has_file_errors on new agreement if moved file has errors
    const newAgreementHasErrors =
      newAgreement?.has_file_errors || !!movedFileHasErrors;

    useFileExplorerStore.setState({
      filesState: {
        ...filesState,
        entities: {
          ...filesState.entities,
          [file_uuid]: {
            ...oldFile,
            ...newFile,
            breadcrumb_name: oldFile.breadcrumb_name,
            parentId: agreement_uuid,
          },
        },
      },
    });
    setAgreementsState({
      ...agreementsState,
      entities: {
        ...agreementsState.entities,
        [oldAgreementUuid]: {
          ...oldAgreement,
          files: oldAgreementRemainingFiles,
          has_file_errors: oldAgreementStillHasErrors,
        },
        [agreement_uuid]: {
          ...newAgreement,
          files: [...(newAgreement?.files || []), file_uuid],
          has_file_errors: newAgreementHasErrors,
        },
      },
    });

    // Persist has_file_errors changes to backend
    if (movedFileHasErrors) {
      try {
        if (oldAgreement && oldAgreement.has_file_errors && !oldAgreementStillHasErrors) {
          await apiService.updateAgreement(oldAgreementUuid, {
            ...oldAgreement,
            has_file_errors: false,
          });
        }
        if (newAgreement && !newAgreement.has_file_errors && newAgreementHasErrors) {
          await apiService.updateAgreement(agreement_uuid, {
            ...newAgreement,
            has_file_errors: true,
          });
        }
      } catch (error) {
        console.error('Failed to update has_file_errors after move:', error);
      }
    }
  },

  // Tags
  getTags: async () => {
    set((state) => ({ isLoading: { ...state.isLoading, tags: true } }));
    try {
      const data = await apiService.getTags();
      set({ tags: data });
    } finally {
      set((state) => ({ isLoading: { ...state.isLoading, tags: false } }));
    }
  },

  // Comments
  getComments: async () => {
    set((state) => ({ isLoading: { ...state.isLoading, comments: true } }));
    try {
      const data = await apiService.getComments();
      set({ comments: data });
    } finally {
      set((state) => ({ isLoading: { ...state.isLoading, comments: false } }));
    }
  },

  // Analytics
  getReportAnalytics: async () => {
    const data = await apiService.getReportAnalytics();
    set({ reportAnalytics: data });
    return data;
  },
}));
